import {
  Container,
  FirstRow,
  Heading,
  InputWrapper,
  RightSide,
} from './UsersPage.styled';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import { useDeleteCompanyUser, useGetUsers } from './hooks';
import { tableConfig, USERS_PER_PAGE } from './constants';
import { useEffect, useState } from 'react';
import { useDebounce } from 'utils/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import Input from 'components/Input/Input';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import { IUserTableDTO } from 'types/User.types';
import { IUser } from 'types/User.types';
import Table from 'components/Table/Table';
import { formatUsersTableData } from 'components/Table/tableDataFormatter';
import Button from 'components/Button/Button';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { AddUserModal } from 'components/Modals/AddUserModal/AddUserModal';

const UsersPage = () => {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const debouncedSearchBy = useDebounce(searchBy);
  usePageTitle('Users');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState<boolean>(false);
  const [userIdForDeletion, setUserIdForDeletion] = useState<number | null>(
    null
  );
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] =
    useState<boolean>(false);

  const onDelete = (rowData: IUserTableDTO) => {
    const userId = Number(rowData.navigationID);
    setUserIdForDeletion(userId);
    setIsDeleteUserModalOpen(true);
  };

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  const { data: usersData, isLoading: isLoadingUsers } = useGetUsers(
    page,
    USERS_PER_PAGE,
    debouncedSearchBy
  );

  const { mutate: deleteCompanyUser, isLoading: isLoadingDeleteUser } =
    useDeleteCompanyUser();

  let tableData: IUserTableDTO[] = [];
  if (usersData?.users) {
    tableData = formatUsersTableData(
      usersData?.users
        .map((user: IUser[]) => {
          return user;
        })
        .flat(),
      t
    );
  }

  return (
    <Container>
      <FirstRow>
        <Heading>{t('User Management')}</Heading>

        <RightSide>
          <InputWrapper>
            <Input
              placeholder={t('Search')}
              width={'100%'}
              onChange={(e: any) => setSearchBy(e.target.value)}
              icon={Search}
              wrapperStyles={{ width: '100%' }}
            />
          </InputWrapper>
          <Button
            width="150rem"
            primary
            label={t('Add user')}
            onClick={() => setIsAddUserModalOpen(true)}
          />
        </RightSide>
      </FirstRow>

      <Table
        page={page}
        perPage={USERS_PER_PAGE}
        total={usersData?.pagination?.total}
        isLoading={isLoadingUsers}
        tableData={tableData}
        tableConfig={tableConfig}
        hasExtraAction={false}
        loadPage={(newPage: number) => setPage(newPage)}
        onDelete={onDelete}
      />

      <DangerModal
        isOpen={isDeleteUserModalOpen}
        setIsOpen={setIsDeleteUserModalOpen}
        title={t('Confirm removal')}
        submit={{
          onClick: () => {
            deleteCompanyUser(userIdForDeletion!);
            setIsDeleteUserModalOpen(false);
            setUserIdForDeletion(null);
          },
          text: t('Remove'),
          disabled: isLoadingDeleteUser,
        }}
        cancel={{
          onClick: () => {
            setIsDeleteUserModalOpen(false);
            setUserIdForDeletion(null);
          },
        }}
        description={t('Are you sure you want to remove this User?')}
      />

      <AddUserModal
        isOpen={isAddUserModalOpen}
        setIsOpen={setIsAddUserModalOpen}
        onCancel={() => setIsAddUserModalOpen(false)}
      />
    </Container>
  );
};

export default UsersPage;
